import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useSite, money } from "@/lib/site";
import { Reveal, iconFor } from "@/components/site/common";
import { Ticker, Approach, CtaBand } from "@/components/site/Sections";
import { CaseCard } from "@/pages/Work";
import { ReviewsMarquee } from "@/components/site/Reviews";

const Hero = () => {
  const { content } = useSite();
  return (
    <section className="relative overflow-hidden grid-lines" data-testid="home-hero">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-sky-300/30 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-32 sm:pt-40 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-12 items-center relative">
        <Reveal className="lg:col-span-7">
          <p className="overline mb-6" data-testid="hero-overline">{content.hero_overline}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight leading-[1.02] text-slate-900" data-testid="hero-title">{content.hero_title}</h1>
          <p className="mt-7 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed" data-testid="hero-subtitle">{content.hero_subtitle}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/services" data-testid="hero-primary-cta" className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-7 py-4 font-semibold transition-colors shadow-[0_12px_30px_rgba(37,99,235,0.35)]">
              Explore services <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" data-testid="hero-secondary-cta" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white/70 px-7 py-4 font-semibold text-slate-800 hover:border-slate-900 transition-colors">
              See pricing
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5 relative">
          <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-[0_30px_80px_rgba(15,23,42,0.18)]">
            <img src={content.hero_image} alt="Team planning a campaign" className="w-full h-full object-cover" data-testid="hero-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#090D16]/70 via-transparent" />
            <p className="absolute bottom-6 left-6 right-6 font-display text-white text-2xl font-bold leading-tight">Think. Build. Grow.<span className="block text-sm font-normal text-slate-300 mt-1">One connected vision</span></p>
          </div>
          <div className="floaty absolute left-3 sm:-left-10 top-10 rounded-2xl bg-white/90 backdrop-blur-xl border border-slate-200 px-5 py-4 shadow-xl">
            <p className="font-mono text-[11px] text-slate-500 uppercase tracking-widest">01 / Promote</p>
            <p className="font-display font-bold text-slate-900">Google & Meta Ads</p>
          </div>
          <div className="floaty absolute -right-3 sm:-right-6 bottom-28 rounded-2xl bg-[#090D16] text-white px-5 py-4 shadow-xl" style={{ animationDelay: "1.5s" }}>
            <p className="font-mono text-[11px] text-sky-400 uppercase tracking-widest">02 / Perform</p>
            <p className="font-display font-bold">Websites that convert</p>
          </div>
        </Reveal>
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-14 relative">
        <p className="font-display text-xl sm:text-2xl text-slate-500 max-w-3xl" data-testid="hero-intro-statement">{content.intro_statement}</p>
      </div>
    </section>
  );
};

const ServicesBento = () => {
  const { packages } = useSite();
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="home-services">
      <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
        <div>
          <p className="overline mb-4">What we do</p>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] max-w-2xl">Marketing with <span className="text-blue-600">momentum.</span></h2>
        </div>
        <Link to="/services" className="inline-flex items-center gap-1.5 font-semibold text-slate-800 hover:text-blue-600 transition-colors" data-testid="home-all-services-link">All services <ArrowRight className="w-4 h-4" /></Link>
      </Reveal>
      <div className="grid md:grid-cols-6 gap-4">
        {packages.map((p, i) => {
          const Icon = iconFor(p.id);
          const wide = i === 0 || i === 3;
          return (
            <Reveal key={p.id} delay={i * 0.06} className={wide ? "md:col-span-4" : "md:col-span-2"}>
              <Link to={`/services/${p.id}`} data-testid={`home-service-card-${p.id}`}
                className={`group relative flex flex-col h-full min-h-[300px] rounded-3xl p-7 sm:p-8 overflow-hidden border transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(15,23,42,0.12)] ${i === 3 ? "bg-[#090D16] text-white border-transparent" : "bg-white border-slate-200"}`}>
                {wide && <img src={p.image} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-90 hidden sm:block [mask-image:linear-gradient(to_left,black_40%,transparent)]" />}
                <div className="relative flex items-center justify-between sm:max-w-[55%] lg:max-w-none">
                  <span className={`grid place-items-center w-12 h-12 rounded-2xl ${i === 3 ? "bg-white/10 text-sky-400" : "bg-blue-50 text-blue-600"}`}><Icon className="w-5 h-5" /></span>
                  {!wide && <ArrowUpRight className="w-5 h-5 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />}
                </div>
                <div className={`relative mt-auto pt-10 ${wide ? "sm:max-w-[50%]" : ""}`}>
                  <p className={`font-mono text-xs uppercase tracking-widest ${i === 3 ? "text-sky-400" : "text-slate-400"}`}>{p.tagline}</p>
                  <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">{p.name}</h3>
                  <p className={`mt-3 text-sm leading-relaxed ${i === 3 ? "text-slate-400" : "text-slate-600"}`}>{p.summary}</p>
                  <p className="mt-5 font-display font-bold">From {money(p.price)} <span className="text-xs font-normal opacity-60">USD</span></p>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const WorkPreview = () => {
  const { cases } = useSite();
  if (!cases.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="home-work">
      <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
        <div>
          <p className="overline mb-4">Selected work</p>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">Results you can <span className="text-blue-600">measure.</span></h2>
        </div>
        <Link to="/work" className="inline-flex items-center gap-1.5 font-semibold text-slate-800 hover:text-blue-600" data-testid="home-all-work-link">All case studies <ArrowRight className="w-4 h-4" /></Link>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-5">
        {cases.slice(0, 3).map((c, i) => <Reveal key={c.id} delay={i * 0.08}><CaseCard c={c} /></Reveal>)}
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <Ticker />
      <ServicesBento />
      <Approach />
      <WorkPreview />
      <ReviewsMarquee />
      <CtaBand />
    </>
  );
}
